'use client';

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useAuth } from '@/context/AuthContext';

export type AppNotificationType = 'ai_analysis_done' | 'ai_analysis_failed';

export type AppNotification = {
  id: string;
  type: AppNotificationType;
  title: string;
  body?: string;
  /** Rota interna do imóvel / destino. */
  href: string;
  hrefLabel?: string;
  opportunityId?: string;
  createdAt: string;
  read: boolean;
};

export type AppNotificationInput = Omit<AppNotification, 'id' | 'createdAt' | 'read'> & {
  id?: string;
};

type NotificationsContextValue = {
  notifications: AppNotification[];
  unreadCount: number;
  push: (input: AppNotificationInput) => void;
  markRead: (id: string) => void;
  markAllRead: () => void;
  remove: (id: string) => void;
  clear: () => void;
};

const MAX_NOTIFICATIONS = 40;
const STORAGE_PREFIX = 'ml.notifications.v1.';

const NotificationsContext = createContext<NotificationsContextValue | null>(null);

/** Fila global: sobrevive a unmount do hook e a corrida de hidratação do provider. */
const pendingQueue: AppNotification[] = [];
const pushListeners = new Set<(item: AppNotification) => void>();

function storageKey(userId: string | null | undefined): string | null {
  if (!userId) return null;
  return `${STORAGE_PREFIX}${userId}`;
}

function loadStored(userId: string | null | undefined): AppNotification[] {
  const key = storageKey(userId);
  if (!key || typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (n): n is AppNotification =>
        !!n &&
        typeof n === 'object' &&
        typeof (n as AppNotification).id === 'string' &&
        typeof (n as AppNotification).title === 'string' &&
        typeof (n as AppNotification).href === 'string'
    );
  } catch {
    return [];
  }
}

function persist(userId: string | null | undefined, items: AppNotification[]) {
  const key = storageKey(userId);
  if (!key || typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, JSON.stringify(items.slice(0, MAX_NOTIFICATIONS)));
  } catch {
    /* quota / private mode */
  }
}

function buildItem(input: AppNotificationInput): AppNotification {
  return {
    id: input.id ?? `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    type: input.type,
    title: input.title,
    body: input.body,
    href: input.href,
    hrefLabel: input.hrefLabel ?? 'Ver imóvel',
    opportunityId: input.opportunityId,
    createdAt: new Date().toISOString(),
    read: false,
  };
}

/**
 * Publica notificação no sininho. Pode ser chamado fora de React
 * (ex.: poll de análise IA após o componente desmontar).
 */
export function publishAppNotification(input: AppNotificationInput): void {
  const item = buildItem(input);
  if (pushListeners.size === 0) {
    pendingQueue.unshift(item);
    if (pendingQueue.length > MAX_NOTIFICATIONS) pendingQueue.length = MAX_NOTIFICATIONS;
    return;
  }
  pushListeners.forEach((listener) => listener(item));
}

export function NotificationsProvider({ children }: { children: React.ReactNode }) {
  const { user, isAuthenticated } = useAuth();
  const userId = isAuthenticated ? (user?.id ?? null) : null;
  const userIdRef = useRef(userId);
  userIdRef.current = userId;
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  /** Evita persistir [] e apagar o localStorage antes de hidratar. */
  const [hydrated, setHydrated] = useState(false);

  const ingest = useCallback((item: AppNotification) => {
    const uid = userIdRef.current;
    setNotifications((prev) => {
      const next = [item, ...prev].slice(0, MAX_NOTIFICATIONS);
      if (uid) persist(uid, next);
      return next;
    });
  }, []);

  useEffect(() => {
    setHydrated(false);
    const stored = loadStored(userId);
    const fromQueue = pendingQueue.splice(0, pendingQueue.length);
    setNotifications(
      fromQueue.length ? [...fromQueue, ...stored].slice(0, MAX_NOTIFICATIONS) : stored
    );
    setHydrated(true);
  }, [userId]);

  useEffect(() => {
    if (!hydrated || !userId) return;
    persist(userId, notifications);
  }, [hydrated, userId, notifications]);

  useEffect(() => {
    pushListeners.add(ingest);
    // Drena fila criada antes do provider montar / sem listeners.
    if (pendingQueue.length) {
      const drained = pendingQueue.splice(0, pendingQueue.length);
      drained.reverse().forEach(ingest);
    }
    return () => {
      pushListeners.delete(ingest);
    };
  }, [ingest]);

  const push = useCallback((input: AppNotificationInput) => {
    publishAppNotification(input);
  }, []);

  const markRead = useCallback((id: string) => {
    setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
  }, []);

  const markAllRead = useCallback(() => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  }, []);

  const remove = useCallback((id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  }, []);

  const clear = useCallback(() => {
    setNotifications([]);
  }, []);

  const unreadCount = useMemo(
    () => notifications.reduce((acc, n) => acc + (n.read ? 0 : 1), 0),
    [notifications]
  );

  const value = useMemo(
    () => ({
      notifications,
      unreadCount,
      push,
      markRead,
      markAllRead,
      remove,
      clear,
    }),
    [notifications, unreadCount, push, markRead, markAllRead, remove, clear]
  );

  return <NotificationsContext.Provider value={value}>{children}</NotificationsContext.Provider>;
}

export function useNotifications(): NotificationsContextValue {
  const ctx = useContext(NotificationsContext);
  if (!ctx) {
    throw new Error('useNotifications deve ser usado dentro de NotificationsProvider');
  }
  return ctx;
}

/** Link padrão para o detalhe com análise do imóvel. */
export function opportunityAnalysisHref(opportunityId: string): string {
  return `/opportunity-details/${encodeURIComponent(opportunityId)}`;
}
